import { Component, OnInit, Input, ViewChild, SecurityContext } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { WorkflowExecutionHistoryService } from '@app/services/workflow-execution-history.service';
import { Messages } from '@app/util/messages.util';

interface EventGroup {
  eventHeader: string;
  rows: string[];
}

@Component({
  selector: 'app-workflow-execution-history',
  templateUrl: './workflow-execution-history.component.html',
  styleUrls: ['./workflow-execution-history.component.scss']
})
export class WorkflowExecutionHistoryComponent implements OnInit {
  @Input() prUrl: string = '';
  @Input() commitId: string = '';
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  
  paginatedTableHtml: string = '';
  isLoading: boolean = false;
  errorMessage: string = '';
  
  // Pagination properties
  totalEvents: number = 0;
  pageSize: number = 10;
  pageIndex: number = 0;
  pageSizeOptions: number[] = [10, 20, 30, 40, 50, 100];

  constructor(
    private activatedRoutes: ActivatedRoute,
    private workflowExecutionHistoryService: WorkflowExecutionHistoryService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    // Extract prUrl (or commitId for push events) from query parameters using snapshot
    const params = this.activatedRoutes.snapshot.queryParams;

    if (params && params['prUrl']) {
      this.prUrl = params['prUrl'];
      this.fetchWorkflowHistory();
    } else if (params && params['commitId']) {
      this.commitId = params['commitId'];
      this.fetchWorkflowHistory();
    }
  }

  fetchWorkflowHistory(): void {

    this.isLoading = true;
    this.errorMessage = '';
    
    // Call API with pagination parameters (page is 1-based for API)
    const apiPage = this.pageIndex + 1;
    
    this.workflowExecutionHistoryService.getWorkflowExecutionHistory(this.prUrl, this.commitId, apiPage, this.pageSize).subscribe({
      next: (response) => {        
        if (response?.output?.text) {
          this.parseAndDisplayTable(response.output.text);
          
          // Use total_count from API response
          if (response.total_count !== undefined) {
            this.totalEvents = response.total_count;
          } 
        } else {
          this.errorMessage = Messages.workflowExecutionHistory.invalidResponse;
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.errorMessage = Messages.workflowExecutionHistory.fetchError;
        this.isLoading = false;
      }
    });
  }

  parseAndDisplayTable(htmlString: string): void {
    // Simply display the HTML from API since API handles pagination
    this.paginatedTableHtml = this.sanitizer.sanitize(SecurityContext.HTML, htmlString) || '';
  }

  onPageChange(event: PageEvent): void {
    const previousPageSize = this.pageSize;
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    
    // Skip API call if all records already fit on a single page
    // e.g. 10 total records, currently showing 10 per page — switching to 20/30/etc needs no fetch
    if (this.totalEvents <= previousPageSize && this.totalEvents <= this.pageSize) {
      return;
    }

    this.fetchWorkflowHistory();
  }

  /**
   * Refreshes the execution history data
   * Resets to first page and re-fetches from API
   */
  refreshData(): void {
    this.pageIndex = 0;
    this.fetchWorkflowHistory();
  }
}
